import { Ant, AntRole, AntState, AntTask, Position } from '@antification/core';
import { antSpriteCache, ANGLE_STEP_SIZE } from './AntSpriteCache';

export interface AntRenderContext {
  tickCount: number;
  selectedAnt: Ant | null;
  hoveredAnt: Ant | null;
  antsOnSurface: Set<number>;
  isAtTarget: (ant: Ant) => boolean;
  getTunnelDirection?: (tunnelId: number, movingTowardEnd: boolean) => { angle: number } | null;
}

/**
 * Convert food hue (0-40) to RGB color values.
 * Hue 0 = ruby red, 20 = tangerine orange, 40 = golden yellow
 */
function hueToFoodRGB(hue: number): { r: number; g: number; b: number } {
  if (hue < 15) {
    return { r: 220, g: 20 + hue * 3, b: 60 };
  } else if (hue < 30) {
    return { r: 255, g: 100 + (hue - 15) * 6, b: 30 };
  } else {
    return { r: 255, g: 200 + (hue - 30) * 2, b: 50 };
  }
}

/**
 * Renders ants with all their visual states and animations.
 * Uses pre-rendered sprite cache for base ant body, with dynamic overlays
 * for carried items and special effects.
 */
export class AntRenderer {
  private spriteCacheInitialized = false;

  /**
   * Render all ants in the colony.
   */
  renderAnts(
    ctx: CanvasRenderingContext2D,
    ants: Map<number, Ant>,
    context: AntRenderContext
  ): void {
    // Initialize sprite cache on first render
    if (!this.spriteCacheInitialized) {
      antSpriteCache.initialize();
      this.spriteCacheInitialized = true;
    }

    for (const ant of ants.values()) {
      if (!ant.isAlive()) continue;
      if (context.antsOnSurface.has(ant.id)) continue;

      this.renderAnt(ctx, ant, context);
    }
  }

  private renderAnt(ctx: CanvasRenderingContext2D, ant: Ant, context: AntRenderContext): void {
    const x = ant.position.x;
    const y = ant.position.y;
    const size = 4 * ant.size;

    // Calculate direction angle
    let angle = 0;
    const inTunnel = ant.currentTunnelId !== null;

    if (inTunnel && context.getTunnelDirection) {
      const tunnelDir = context.getTunnelDirection(ant.currentTunnelId!, ant.movingTowardEnd);
      if (tunnelDir) {
        angle = tunnelDir.angle;
      }
    } else if (ant.targetPosition) {
      const dirX = ant.targetPosition.x - ant.position.x;
      const dirY = ant.targetPosition.y - ant.position.y;
      if (Math.abs(dirX) > 0.1 || Math.abs(dirY) > 0.1) {
        angle = Math.atan2(dirY, dirX);
      }
    }

    // Try to use cached sprite
    const isSelected = ant === context.selectedAnt;
    const isHovered = ant === context.hoveredAnt;
    const sprite = antSpriteCache.getFormicariumSprite(ant.role, angle, isSelected, isHovered);

    if (sprite) {
      // Use cached sprite - single drawImage call instead of ~10 draw operations
      // Sprites are rendered at higher resolution; draw scaled to world-space size
      const dim = antSpriteCache.getFormicariumDimensions();
      ctx.drawImage(
        sprite.canvas,
        x - dim.centerX,
        y - dim.centerY,
        dim.width,
        dim.height
      );

      // Calculate positions for overlays based on quantized angle
      const quantizedAngle = sprite.angleIdx * ANGLE_STEP_SIZE;
      const cos = Math.cos(quantizedAngle);
      const sin = Math.sin(quantizedAngle);

      const abdomenX = x - cos * size * 1.3;
      const abdomenY = y - sin * size * 1.3;
      const headX = x + cos * size * 1.1;
      const headY = y + sin * size * 1.1;

      // Render carried items and special effects (dynamic, not cached)
      this.renderCarriedItems(ctx, ant, abdomenX, abdomenY, headX, headY, size);
      this.renderSpecialEffects(ctx, ant, x, y, headX, headY, size, cos, context.tickCount, size * 0.9);
    } else {
      // Fallback to direct rendering if cache not available
      this.renderAntDirect(ctx, ant, x, y, size, angle, context);
    }
  }

  /**
   * Direct rendering fallback (original implementation).
   */
  private renderAntDirect(
    ctx: CanvasRenderingContext2D,
    ant: Ant,
    x: number,
    y: number,
    size: number,
    angle: number,
    context: AntRenderContext
  ): void {
    const { color, darkerColor } = this.getAntColors(ant, context);

    ctx.fillStyle = color;
    ctx.strokeStyle = darkerColor;
    ctx.lineWidth = 0.5;

    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Abdomen (rear, largest)
    const abdomenX = x - cos * size * 1.3;
    const abdomenY = y - sin * size * 1.3;
    ctx.beginPath();
    ctx.ellipse(abdomenX, abdomenY, size * 1.2, size * 0.8, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Thorax (middle)
    ctx.beginPath();
    ctx.ellipse(x, y, size * 0.8, size * 0.55, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Head (front, smallest)
    const headX = x + cos * size * 1.1;
    const headY = y + sin * size * 1.1;
    ctx.beginPath();
    ctx.ellipse(headX, headY, size * 0.55, size * 0.45, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Antennae
    ctx.strokeStyle = color;
    ctx.lineWidth = 0.7;
    const antennaLen = size * 1.0;
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle - 0.6) * antennaLen, headY + Math.sin(angle - 0.6) * antennaLen);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle + 0.6) * antennaLen, headY + Math.sin(angle + 0.6) * antennaLen);
    ctx.stroke();

    // Legs (3 pairs)
    ctx.strokeStyle = darkerColor;
    ctx.lineWidth = 0.5;
    const legLen = size * 0.9;
    for (let i = -1; i <= 1; i++) {
      const legBaseX = x + cos * size * i * 0.4;
      const legBaseY = y + sin * size * i * 0.4;
      // Left leg
      const leftAngle = angle - Math.PI / 2 - 0.3 + i * 0.2;
      ctx.beginPath();
      ctx.moveTo(legBaseX, legBaseY);
      ctx.lineTo(legBaseX + Math.cos(leftAngle) * legLen, legBaseY + Math.sin(leftAngle) * legLen);
      ctx.stroke();
      // Right leg
      const rightAngle = angle + Math.PI / 2 + 0.3 - i * 0.2;
      ctx.beginPath();
      ctx.moveTo(legBaseX, legBaseY);
      ctx.lineTo(legBaseX + Math.cos(rightAngle) * legLen, legBaseY + Math.sin(rightAngle) * legLen);
      ctx.stroke();
    }

    // Render carried items and special effects
    this.renderCarriedItems(ctx, ant, abdomenX, abdomenY, headX, headY, size);
    this.renderSpecialEffects(ctx, ant, x, y, headX, headY, size, cos, context.tickCount, legLen);
  }

  private getAntColors(ant: Ant, context: AntRenderContext): { color: string; darkerColor: string } {
    let color = '#8b6914';
    let darkerColor = '#5a4510';

    if (ant.role === AntRole.QUEEN) {
      color = '#b8860b';
      darkerColor = '#8b6508';
    } else if (ant.role === AntRole.SOLDIER) {
      color = '#654321';
      darkerColor = '#4a3018';
    } else if (ant.role === AntRole.ENGINEER) {
      color = '#6b5a4a';
      darkerColor = '#4a3a2a';
    } else if (ant.role === AntRole.NURSE) {
      color = '#7a6520';
      darkerColor = '#5a4510';
    }

    // Highlight colors for selection
    if (ant === context.selectedAnt) {
      color = '#ffd700';
      darkerColor = '#b8960b';
    } else if (ant === context.hoveredAnt) {
      color = '#daa520';
      darkerColor = '#a07810';
    }

    return { color, darkerColor };
  }

  private renderCarriedItems(
    ctx: CanvasRenderingContext2D,
    ant: Ant,
    abdomenX: number,
    abdomenY: number,
    headX: number,
    headY: number,
    size: number
  ): void {
    // Food indicator - color matches the carried food
    if (ant.carryingFood && ant.carriedFoodAmount > 0) {
      const foodRGB = hueToFoodRGB(ant.carriedFoodHue);
      ctx.fillStyle = `rgb(${foodRGB.r}, ${foodRGB.g}, ${foodRGB.b})`;
      ctx.beginPath();
      ctx.arc(abdomenX, abdomenY - size * 1.2, size * 0.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255, 255, 200, 0.6)';
      ctx.beginPath();
      ctx.arc(abdomenX - size * 0.1, abdomenY - size * 1.3, size * 0.15, 0, Math.PI * 2);
      ctx.fill();
    }

    // Egg indicator
    if (ant.carryingEgg) {
      ctx.fillStyle = '#f5f5dc';
      ctx.beginPath();
      ctx.ellipse(headX, headY - size * 1.0, size * 0.5, size * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.beginPath();
      ctx.ellipse(headX - size * 0.1, headY - size * 1.1, size * 0.25, size * 0.15, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderSpecialEffects(
    ctx: CanvasRenderingContext2D,
    ant: Ant,
    x: number,
    y: number,
    headX: number,
    headY: number,
    size: number,
    cos: number,
    tickCount: number,
    legLen: number
  ): void {
    // Scar indicator
    if (ant.scarred) {
      ctx.strokeStyle = '#2a1008';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - size * 0.6, y - size * 0.4);
      ctx.lineTo(x + size * 0.4, y + size * 0.4);
      ctx.stroke();
    }

    // Contemplating indicator
    if (ant.state === AntState.CONTEMPLATING) {
      ctx.fillStyle = 'rgba(255, 255, 200, 0.4)';
      ctx.beginPath();
      ctx.arc(headX + cos * size * 0.8, headY - size * 1.5, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(headX + cos * size * 0.5, headY - size * 1.0, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Transitioning indicator
    if (ant.task === AntTask.TRANSITIONING) {
      ctx.strokeStyle = '#b8860b';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, size * 2, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Engineer working animation
    if (ant.task === AntTask.REPAIRING) {
      this.renderWorkingAnimation(ctx, ant, x, y, size, tickCount, legLen);
    }
  }

  private renderWorkingAnimation(
    ctx: CanvasRenderingContext2D,
    ant: Ant,
    x: number,
    y: number,
    size: number,
    tickCount: number,
    legLen: number
  ): void {
    const workTime = tickCount * 0.08;
    const workCenterX = x;
    const workCenterY = y;

    // Ant moves in a small circle around the work point
    const orbitRadius = size * 1.5;
    const orbitAngle = workTime * 2;
    const orbitX = workCenterX + Math.cos(orbitAngle) * orbitRadius * 0.5;
    const orbitY = workCenterY + Math.sin(orbitAngle) * orbitRadius * 0.3;

    const faceAngle = Math.atan2(workCenterY - orbitY, workCenterX - orbitX) + Math.sin(workTime * 5) * 0.3;
    const workHeadX = orbitX + Math.cos(faceAngle) * size * 1.1;
    const workHeadY = orbitY + Math.sin(faceAngle) * size * 1.1;

    // Animated digging motion
    ctx.strokeStyle = '#8b7355';
    ctx.lineWidth = 1.5;
    const digSpeed = 12;

    for (let side = -1; side <= 1; side += 2) {
      const digOffset = Math.sin(workTime * digSpeed + side * 2) * 0.5;
      const legAngle = faceAngle + side * 0.4 + digOffset;
      ctx.beginPath();
      ctx.moveTo(workHeadX, workHeadY);
      ctx.lineTo(
        workHeadX + Math.cos(legAngle) * legLen * 1.4,
        workHeadY + Math.sin(legAngle) * legLen * 1.4
      );
      ctx.stroke();
    }

    // Dirt particles
    ctx.fillStyle = 'rgba(139, 90, 43, 0.8)';
    for (let i = 0; i < 6; i++) {
      const particleTime = (workTime * 4 + i * 1.2) % 5;
      const particleAngle = faceAngle + Math.PI + (i - 2.5) * 0.5 + Math.sin(i * 2.3) * 0.3;
      const particleDist = particleTime * 6;
      const particleSize = Math.max(0, 2.5 - particleTime * 0.5);

      if (particleSize > 0) {
        const px = workCenterX + Math.cos(particleAngle) * particleDist;
        const py = workCenterY + Math.sin(particleAngle) * particleDist - particleTime * 3;
        ctx.beginPath();
        ctx.arc(px, py, particleSize, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Work site indicator
    const pulse = 0.8 + Math.sin(workTime * 4) * 0.2;
    ctx.strokeStyle = 'rgba(255, 200, 100, 0.5)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(workCenterX, workCenterY, size * 2 * pulse, 0, Math.PI * 2);
    ctx.stroke();

    // Progress indicator
    const repairTarget = ant.repairTarget;
    const maxTime = repairTarget?.type === 'tunnel' ? 15000 : 30000;
    const progress = Math.min(1, ant.taskTimer / maxTime);
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(workCenterX, workCenterY - size * 3.5, size * 1.5, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${Math.round(progress * 100)}%`, workCenterX, workCenterY - size * 3.5 + 4);

    // Tool icon
    const toolRotation = Math.sin(workTime * 6) * 0.3;
    ctx.save();
    ctx.translate(workCenterX + size * 2.5, workCenterY - size * 2);
    ctx.rotate(toolRotation);
    ctx.fillStyle = '#a0a0a0';
    ctx.strokeStyle = '#606060';
    ctx.lineWidth = 1;
    const toolSize = size * 0.5;
    ctx.fillRect(-toolSize * 0.15, -toolSize * 0.8, toolSize * 0.3, toolSize * 1.6);
    ctx.beginPath();
    ctx.moveTo(-toolSize * 0.8, -toolSize * 0.8);
    ctx.lineTo(toolSize * 0.8, -toolSize * 0.8);
    ctx.lineTo(toolSize * 0.4, -toolSize * 0.4);
    ctx.lineTo(-toolSize * 0.4, -toolSize * 0.4);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Render hover tooltip for an ant.
   */
  renderAntHover(
    ctx: CanvasRenderingContext2D,
    ant: Ant,
    scale: number,
    offsetX: number,
    offsetY: number
  ): void {
    const worldX = ant.position.x * scale + offsetX;
    const worldY = ant.position.y * scale + offsetY;

    // Draw highlight ring
    ctx.save();
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.arc(worldX, worldY, 12 * scale, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Tooltip
    const name = ant.getDisplayName();
    const role = ant.role.charAt(0).toUpperCase() + ant.role.slice(1);
    const task = ant.task.replace(/_/g, ' ').toLowerCase() || 'idle';

    const tooltipWidth = 140;
    const tooltipHeight = 50;
    const tooltipX = worldX + 15;
    const tooltipY = worldY - tooltipHeight - 5;

    ctx.fillStyle = 'rgba(30, 20, 10, 0.95)';
    ctx.strokeStyle = '#daa520';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight, 6);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 12px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(name, tooltipX + 10, tooltipY + 16);

    ctx.fillStyle = '#a08050';
    ctx.font = '11px "Segoe UI", system-ui, sans-serif';
    ctx.fillText(`${role}`, tooltipX + 10, tooltipY + 32);
    ctx.fillStyle = '#888';
    ctx.fillText(`${task}`, tooltipX + 10, tooltipY + 45);
  }
}
